import { BACKEND_URL } from "./lib/backend";
/**
 * TravelBriefPage — Public, SEO-indexed Brief of the Week.
 * Routes: /travel-brief (current) · /travel-brief/:slug (permalink e.g. 2026-W14)
 */
import React, { useState, useEffect, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { RefreshCw, ChevronLeft, ChevronRight, Archive } from "lucide-react";
import PublicPageNav from "./components/PublicPageNav";
import PublicPageFooter from "./components/PublicPageFooter";

const API = `${BACKEND_URL}/api`;
const SITE = "https://tourismintel.ai";

// ── Slug helpers ────────────────────────────────────────────────────────────
const parseSlug = (s) => {
  const [y, w] = s.split("-W");
  return { year: parseInt(y, 10), week: parseInt(w, 10) };
};
const makeSlug = (year, week) => `${year}-W${String(week).padStart(2, "0")}`;
const prevSlug = (s) => {
  const { year, week } = parseSlug(s);
  return week <= 1 ? makeSlug(year - 1, 52) : makeSlug(year, week - 1);
};
const nextSlug = (s) => {
  const { year, week } = parseSlug(s);
  return week >= 52 ? makeSlug(year + 1, 1) : makeSlug(year, week + 1);
};
const slugLabel = (s) => {
  const { year, week } = parseSlug(s);
  return `Week ${week}, ${year}`;
};

// ── Share SVG icons ─────────────────────────────────────────────────────────
const XIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.746l7.73-8.835L1.254 2.25H8.08l4.253 5.622 5.91-5.622Zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);
const LinkedInIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
  </svg>
);

// ── Meta tag helpers ────────────────────────────────────────────────────────
const setMeta = (name, content, prop = false) => {
  const sel = prop ? `meta[property="${name}"]` : `meta[name="${name}"]`;
  const el = document.querySelector(sel);
  if (el) el.setAttribute("content", content);
};
const setCanonical = (url) => {
  const el = document.querySelector('link[rel="canonical"]');
  if (el) el.setAttribute("href", url);
};

function injectJsonLd(brief, pageUrl, ogImageUrl) {
  document.getElementById("brief-jsonld")?.remove();
  const script = document.createElement("script");
  script.id = "brief-jsonld";
  script.type = "application/ld+json";
  script.textContent = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "Article",
    headline: "Travel Intelligence Brief — " + (brief.slug ? slugLabel(brief.slug) : "Weekly"),
    description: (brief.content || "").slice(0, 160),
    ...(brief.generated_at ? { datePublished: new Date(brief.generated_at).toISOString() } : {}),
    ...(brief.updated_at ? { dateModified: new Date(brief.updated_at).toISOString() } : {}),
    author: { "@type": "Person", name: brief.author_name || "Mirko Lalli", url: SITE },
    publisher: {
      "@type": "NewsMediaOrganization",
      name: "TourismIntel",
      url: SITE,
      logo: { "@type": "ImageObject", url: `${SITE}/logo512.png`, width: 512, height: 512 },
    },
    mainEntityOfPage: { "@type": "WebPage", "@id": pageUrl },
    image: ogImageUrl || `${SITE}/og-image.jpg?v=20261029`,
  });
  document.head.appendChild(script);
}

function injectFaqJsonLd(brief) {
  document.getElementById("brief-faq-jsonld")?.remove();
  if (!Array.isArray(brief.faqs) || brief.faqs.length === 0) return;
  const s = document.createElement("script");
  s.id = "brief-faq-jsonld";
  s.type = "application/ld+json";
  s.textContent = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: brief.faqs.map(f => ({
      "@type": "Question",
      name: f.question,
      acceptedAnswer: { "@type": "Answer", text: f.answer },
    })),
  });
  document.head.appendChild(s);
}

// ── Share component ─────────────────────────────────────────────────────────
function ShareButtons({ brief, pageUrl }) {
  const firstPara = (brief?.content?.split("\n\n")[0] || "").slice(0, 220).trim();
  const tweetText = `${firstPara}…\n\nFull brief 👇`;
  const xUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(tweetText)}&url=${encodeURIComponent(pageUrl)}`;
  const liUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pageUrl)}`;

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
      <span style={{ fontSize: 12, color: "#6b7a7d", fontWeight: 600, letterSpacing: "0.05em", textTransform: "uppercase" }}>Share</span>
      <a
        href={xUrl}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="share-x-btn"
        style={{
          display: "inline-flex", alignItems: "center", gap: 6,
          padding: "8px 14px", borderRadius: 99, textDecoration: "none",
          fontSize: 13, fontWeight: 600, letterSpacing: "-0.01em",
          background: "#000", color: "#fff",
          transition: "opacity 0.15s",
        }}
        onMouseEnter={e => e.currentTarget.style.opacity = "0.82"}
        onMouseLeave={e => e.currentTarget.style.opacity = "1"}
      >
        <XIcon /> Post on X
      </a>
      <a
        href={liUrl}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="share-linkedin-btn"
        style={{
          display: "inline-flex", alignItems: "center", gap: 6,
          padding: "8px 14px", borderRadius: 99, textDecoration: "none",
          fontSize: 13, fontWeight: 600, letterSpacing: "-0.01em",
          background: "#0a66c2", color: "#fff",
          transition: "opacity 0.15s",
        }}
        onMouseEnter={e => e.currentTarget.style.opacity = "0.82"}
        onMouseLeave={e => e.currentTarget.style.opacity = "1"}
      >
        <LinkedInIcon /> Share on LinkedIn
      </a>
    </div>
  );
}

function ArchiveGrid({ archive, currentSlug }) {
  if (!archive.length) return null;
  const fmt = (d) => d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "";
  return (
    <section style={{ marginTop: 64 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Archive style={{ width: 16, height: 16, color: "#006874" }} />
          <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700, letterSpacing: "-0.01em", color: "#191c1d" }}>
            Past Briefs
          </h2>
        </div>
        <Link to="/briefs" style={{ fontSize: 13, color: "#006874", textDecoration: "none", fontWeight: 500 }}>
          Full archive →
        </Link>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 10 }}>
        {archive.map((b) => {
          const linkSlug = b.seo_slug || b.slug;
          const isCurrent = b.slug === currentSlug || b.seo_slug === currentSlug;
          return (
            <Link
              key={linkSlug}
              to={`/travel-brief/${linkSlug}`}
              data-testid={`archive-brief-${b.slug}`}
              style={{
                display: "block", textDecoration: "none", padding: "14px 16px",
                borderRadius: 12, border: `2px solid ${isCurrent ? "#006874" : "#e8ecee"}`,
                background: isCurrent ? "#f0f9fa" : "#fff",
                transition: "border-color 0.15s, background 0.15s",
              }}
              onMouseEnter={e => { if (!isCurrent) { e.currentTarget.style.borderColor = "#006874"; e.currentTarget.style.background = "#f8fdfd"; }}}
              onMouseLeave={e => { if (!isCurrent) { e.currentTarget.style.borderColor = "#e8ecee"; e.currentTarget.style.background = "#fff"; }}}
            >
              <p style={{ margin: 0, fontSize: 11, fontWeight: 700, color: "#006874", letterSpacing: "0.05em", textTransform: "uppercase" }}>
                {b.slug}
              </p>
              <p style={{ margin: "4px 0 2px", fontSize: 13, fontWeight: 600, color: "#191c1d", lineHeight: 1.3 }}>
                {b.title || "Travel Intelligence Brief"}
              </p>
              <p style={{ margin: 0, fontSize: 11, color: "#6b7a7d" }}>{fmt(b.generated_at)}</p>
              {b.preview && (
                <p style={{ margin: "6px 0 0", fontSize: 12, color: "#3b494c", lineHeight: 1.5, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                  {b.preview}
                </p>
              )}
            </Link>
          );
        })}
      </div>
    </section>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function TravelBriefPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [brief, setBrief] = useState(null);
  const [archive, setArchive] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const pageUrl = slug ? `${SITE}/travel-brief/${slug}` : `${SITE}/travel-brief`;

  // ISO slug for prev/next navigation — brief.slug is always ISO (e.g. 2026-W14)
  // slug from URL may be a SEO slug (e.g. "ai-disrupts-hotel-pricing")
  const isoSlug = brief?.slug || (slug && /^\d{4}-W\d{2}$/.test(slug) ? slug : null);

  const load = useCallback(async () => {
    setLoading(true);
    setNotFound(false);
    try {
      const [briefRes, archiveRes] = await Promise.allSettled([
        slug ? axios.get(`${API}/briefs/${slug}`) : axios.get(`${API}/brief`),
        axios.get(`${API}/briefs?limit=50`),
      ]);
      if (briefRes.status === "fulfilled") {
        setBrief(briefRes.value.data.brief || null);
      } else {
        if (briefRes.reason?.response?.status === 404) setNotFound(true);
        setBrief(null);
      }
      if (archiveRes.status === "fulfilled") {
        setArchive(archiveRes.value.data.briefs || []);
      }
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => { load(); }, [load]);

  // Dynamic SEO — use editorial title if available.
  // Deps intentionally limited to reactive values. SITE, setMeta, setCanonical, injectJsonLd
  // and briefSlugForOg are module-level constants / stable helpers derived from deps already listed.
   
  useEffect(() => {
    const cleanup = [];
    if (brief?.content) {
      const editorialTitle = brief.title;
      const isoLabel = isoSlug ? slugLabel(isoSlug) : "Weekly";
      const pageTitle = editorialTitle
        ? `${editorialTitle} | TourismIntel`
        : `${isoLabel} — Travel Intelligence Brief | TourismIntel`;
      document.title = pageTitle;
      const desc = (brief.content || "").slice(0, 155).replace(/\s+/g, " ").trim() + "…";
      // Dynamic OG image pointing to the brief image generation endpoint
      const briefSlugForOg = isoSlug || slug;
      const ogImageUrl = briefSlugForOg
        ? `${BACKEND_URL}/api/og/brief/${briefSlugForOg}`
        : `${SITE}/og-image.jpg?v=20261029`;
      // Canonical always points to the preferred slug (seo_slug > iso slug).
      // Prevents ISO-slug duplicates (e.g. /2026-W20 vs /hotel-invisible-ai-agents-existence).
      const canonicalUrl = slug
        ? `${SITE}/travel-brief/${brief.seo_slug || brief.slug || slug}`
        : pageUrl;
      setMeta("description", desc);
      setMeta("og:title", pageTitle, true);
      setMeta("og:description", desc, true);
      setMeta("og:url", canonicalUrl, true);
      setMeta("og:image", ogImageUrl, true);
      setMeta("og:image:width", "1200", true);
      setMeta("og:image:height", "630", true);
      setMeta("twitter:title", pageTitle, true);
      setMeta("twitter:description", desc, true);
      setMeta("twitter:image", ogImageUrl, false);
      setMeta("twitter:card", "summary_large_image", false);
      setCanonical(canonicalUrl);
      injectJsonLd(brief, canonicalUrl, ogImageUrl);
      cleanup.push(() => document.getElementById("brief-jsonld")?.remove());
      injectFaqJsonLd(brief);
      cleanup.push(() => document.getElementById("brief-faq-jsonld")?.remove());
      // Ensure robots is indexable when we have content
      const robotsEl = document.querySelector('meta[name="robots"]');
      if (robotsEl) robotsEl.setAttribute("content", "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1");
    } else if (!loading && notFound) {
      // noindex ONLY on a confirmed 404 from the API.
      // Transient errors (429/5xx/network) must NOT noindex a real page during Googlebot render.
      document.title = "Travel Intelligence Brief | TourismIntel";
      const robots = document.querySelector('meta[name="robots"]');
      if (robots) {
        robots.setAttribute("content", "noindex, follow");
        cleanup.push(() => robots.setAttribute("content", "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"));
      }
    }
    // Reset to the route's own URL (never the homepage: a homepage canonical on a 404/transition
    // is exactly the "Duplicate, Google chose different canonical" signal GSC reported).
    return () => { cleanup.forEach(fn => fn()); setCanonical(`${SITE}${window.location.pathname.replace(/\/+$/, "") || "/"}`); };
  }, [brief, slug, isoSlug, pageUrl, loading, notFound]);

  const formatDate = (d) => d
    ? new Date(d).toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" })
    : "";

  // Permalink nav — use ISO slug from brief doc for prev/next calculation
  const hasPrev = isoSlug && archive.some(b => b.slug === prevSlug(isoSlug));
  const hasNext = isoSlug && archive.some(b => b.slug === nextSlug(isoSlug));

  return (
    <div style={{ background: "#f8f9fa", minHeight: "100vh", fontFamily: "'Space Grotesk', sans-serif" }}>

      {/* Nav — shared across all public pages */}
      <PublicPageNav active="brief" />

      {/* Content */}
      <main style={{ maxWidth: 740, margin: "0 auto", padding: "56px 24px 96px" }}>

        {loading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: "80px 0" }}>
            <RefreshCw style={{ width: 24, height: 24, color: "#006874", animation: "spin 1s linear infinite" }} />
            <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
          </div>
        ) : notFound ? (
          <div style={{ textAlign: "center", padding: "80px 0" }}>
            <p style={{ color: "#191c1d", fontSize: 18, fontWeight: 600, marginBottom: 8 }}>Brief not found</p>
            <p style={{ color: "#6b7a7d", fontSize: 15, marginBottom: 24 }}>
              {slug ? `No published brief found for ${slug}.` : "No brief available yet — check back soon."}
            </p>
            <Link to="/travel-brief" style={{ color: "#006874", textDecoration: "none", fontWeight: 500 }}>← View latest brief</Link>
            <ArchiveGrid archive={archive} currentSlug={slug} />
          </div>
        ) : !brief?.content ? (
          <div style={{ textAlign: "center", padding: "80px 0" }}>
            <p style={{ color: "#6b7a7d", fontSize: 16, marginBottom: 24 }}>No brief published yet — check back soon.</p>
            <Link to="/" style={{ color: "#006874", textDecoration: "none", fontWeight: 500 }}>← Back to TourismIntel</Link>
          </div>
        ) : (
          <>
            {/* Permalink nav — only on /travel-brief/:slug */}
            {slug && (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 36 }}>
                <button
                  onClick={() => hasPrev && navigate(`/travel-brief/${prevSlug(isoSlug)}`)}
                  data-testid="prev-brief-btn"
                  disabled={!hasPrev}
                  style={{
                    display: "flex", alignItems: "center", gap: 4, padding: "7px 14px",
                    borderRadius: 99, border: "1.5px solid #e8ecee", background: "#fff",
                    fontSize: 13, fontWeight: 500, color: hasPrev ? "#191c1d" : "#bac9cc",
                    cursor: hasPrev ? "pointer" : "not-allowed", transition: "all 0.15s",
                  }}
                >
                  <ChevronLeft style={{ width: 14, height: 14 }} />
                  {hasPrev ? slugLabel(prevSlug(isoSlug)) : "No earlier brief"}
                </button>
                <span style={{ fontSize: 12, fontWeight: 700, color: "#006874", letterSpacing: "0.08em", textTransform: "uppercase" }}>
                  {isoSlug ? slugLabel(isoSlug) : slug}
                </span>
                <button
                  onClick={() => hasNext && navigate(`/travel-brief/${nextSlug(isoSlug)}`)}
                  data-testid="next-brief-btn"
                  disabled={!hasNext}
                  style={{
                    display: "flex", alignItems: "center", gap: 4, padding: "7px 14px",
                    borderRadius: 99, border: "1.5px solid #e8ecee", background: "#fff",
                    fontSize: 13, fontWeight: 500, color: hasNext ? "#191c1d" : "#bac9cc",
                    cursor: hasNext ? "pointer" : "not-allowed", transition: "all 0.15s",
                  }}
                >
                  {hasNext ? slugLabel(nextSlug(isoSlug)) : "No later brief"}
                  <ChevronRight style={{ width: 14, height: 14 }} />
                </button>
              </div>
            )}

            <article>
              {/* Label */}
              <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "#006874", marginBottom: 14 }}>
                {isoSlug ? `Travel Intelligence · ${slugLabel(isoSlug)}` : "Brief of the Week"}
              </p>

              {/* Headline — editorial title if available */}
              <h1 style={{ fontFamily: "'Newsreader', serif", fontSize: "clamp(1.9rem, 5vw, 2.9rem)", fontWeight: 500, lineHeight: 1.15, color: "#191c1d", marginBottom: 24, letterSpacing: "-0.02em" }}>
                {brief.title || "Weekly Travel Intelligence Brief"}
              </h1>

              {/* Author + date */}
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 36, paddingBottom: 28, borderBottom: "1px solid #e8ecee" }}>
                {brief.author_photo_url ? (
                  <img src={brief.author_photo_url} alt={brief.author_name || "Mirko Lalli"}
                    style={{ width: 44, height: 44, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }}
                    onError={e => { e.target.style.display = "none"; }}
                  />
                ) : (
                  <div style={{ width: 44, height: 44, borderRadius: "50%", background: "#e8ecee", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Newsreader', serif", fontWeight: 700, fontSize: 16, color: "#006874", flexShrink: 0 }}>
                    ML
                  </div>
                )}
                <div>
                  <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: "#191c1d" }}>{brief.author_name || "Mirko Lalli"}</p>
                  <p style={{ margin: 0, fontSize: 12, color: "#6b7a7d" }}>{formatDate(brief.generated_at)}</p>
                </div>
              </div>

              {/* Brief body */}
              <div style={{ fontFamily: "'Newsreader', serif", fontSize: "clamp(1.05rem, 2.5vw, 1.2rem)", lineHeight: 1.82, color: "#2d3637", marginBottom: 40 }}>
                {(brief.content || "").split("\n\n").map((para, i) => (
                   
                  <p key={`para-${i}`} style={{ marginBottom: "1.4em" }}>{para}</p>
                ))}
              </div>

              {/* FAQ — key questions (SEO long-tail + FAQPage JSON-LD) */}
              {Array.isArray(brief.faqs) && brief.faqs.length > 0 && (
                <section data-testid="brief-faq-section" style={{ marginBottom: 40 }}>
                  <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "#006874", marginBottom: 18 }}>
                    Key questions this week
                  </p>
                  {brief.faqs.map((f, i) => (
                    <div
                      key={`faq-${f.question}`}
                      data-testid={`brief-faq-item-${i}`}
                      style={{ marginBottom: 22, paddingBottom: 22, borderBottom: i < brief.faqs.length - 1 ? "1px solid #e8ecee" : "none" }}
                    >
                      <h3 style={{ fontFamily: "'Newsreader', serif", fontSize: "1.08rem", fontWeight: 600, color: "#191c1d", margin: "0 0 8px" }}>
                        {f.question}
                      </h3>
                      <p style={{ fontSize: 14.5, lineHeight: 1.7, color: "#3b494c", margin: 0 }}>{f.answer}</p>
                    </div>
                  ))}
                </section>
              )}

              {/* News in this brief — discreet source list (iter139, per-brief page ONLY, never the home accordion) */}
              {Array.isArray(brief.source_articles) && brief.source_articles.length > 0 && (
                <section data-testid="brief-sources-section" style={{ marginBottom: 40 }}>
                  <p style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "#9aa7a9", marginBottom: 10 }}>
                    News in this brief
                  </p>
                  <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
                    {brief.source_articles.map((s, i) => (
                      <li key={s.url || `src-${i}`} style={{ marginBottom: 5 }}>
                        <a
                          href={s.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`brief-source-link-${i}`}
                          className="hover:underline"
                          style={{ fontSize: 13, color: "#6b7a7d", textDecoration: "none", lineHeight: 1.55 }}
                        >
                          {s.title}
                        </a>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {/* Divider + share + CTA */}
              <div style={{ borderTop: "2px solid #006874", paddingTop: 28, marginBottom: 32, width: 48 }} />

              {/* Share buttons */}
              <div style={{ marginBottom: 40 }}>
                <ShareButtons brief={brief} pageUrl={pageUrl} />
              </div>

              {/* Permalink anchor — shown on /travel-brief (current brief) */}
              {brief.seo_slug && !slug && (
                <div style={{ marginBottom: 40 }}>
                  <a
                    href={`/travel-brief/${brief.seo_slug}`}
                    data-testid="permalink-anchor"
                    style={{ fontSize: 13, color: "#006874", textDecoration: "none", fontWeight: 500 }}
                  >
                    Permalink: tourismintel.ai/travel-brief/{brief.seo_slug} →
                  </a>
                </div>
              )}
              {brief.slug && !brief.seo_slug && !slug && (
                <div style={{ marginBottom: 40 }}>
                  <a
                    href={`/travel-brief/${brief.slug}`}
                    data-testid="permalink-anchor"
                    style={{ fontSize: 13, color: "#006874", textDecoration: "none", fontWeight: 500 }}
                  >
                    Permalink: tourismintel.ai/travel-brief/{brief.slug} →
                  </a>
                </div>
              )}

              {/* CTA */}
              <div style={{ background: "#fff", borderRadius: 16, padding: "28px 32px", border: "1px solid #e8ecee", textAlign: "center" }}>
                <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "#006874", marginBottom: 10 }}>
                  AI-Curated · Updated every 12h
                </p>
                <h2 style={{ fontFamily: "'Newsreader', serif", fontSize: "clamp(1.3rem, 4vw, 1.6rem)", fontWeight: 500, color: "#191c1d", marginBottom: 10, lineHeight: 1.3 }}>
                  Join 30+ travel professionals getting the full intelligence feed
                </h2>
                <p style={{ fontSize: 14, color: "#6b7a7d", marginBottom: 22, lineHeight: 1.6 }}>
                  Curated articles on travel tech, hotel innovation and destination strategy — updated every 12 hours. Free to join.
                </p>
                <Link
                  to="/"
                  style={{ display: "inline-block", background: "#006874", color: "#fff", padding: "13px 28px", borderRadius: 99, textDecoration: "none", fontWeight: 600, fontSize: 14 }}
                >
                  Access the full feed →
                </Link>
              </div>
            </article>

            {/* Archive grid — only on /travel-brief (current) */}
            {!slug && <ArchiveGrid archive={archive} currentSlug={null} />}

            {/* On permalink page, show full archive below nav */}
            {slug && archive.length > 0 && (
              <ArchiveGrid archive={archive} currentSlug={slug} />
            )}
          </>
        )}
      </main>

      {/* Footer */}
      <PublicPageFooter />
    </div>
  );
}
